import React, { useEffect, useState } from 'react';
import API from '../services/api';
import './MistakeBank.css';

const CATEGORY_COLORS = {
    quantitative: '#0f3460',
    logical: '#8e44ad',
    verbal: '#16a085',
    programming: '#e94560',
    sql: '#f39c12',
};

const DIFF_COLORS = {
    easy: { bg: '#e6f9f0', color: '#1a7f3c' },
    medium: { bg: '#fff8e6', color: '#b37400' },
    hard: { bg: '#fff0f0', color: '#cc0000' },
};

const MistakeBank = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [topicFilter, setTopicFilter] = useState('all');
    const [activeTab, setActiveTab] = useState('active');

    useEffect(() => { fetchMistakes(); }, []);

    const fetchMistakes = async () => {
        try {
            const res = await API.get('/mistakes');
            setData(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return (
        <div className="s-loading">Loading mistake bank...</div>
    );

    const { mistakes = [], stats = {}, topicBreakdown = [] } = data || {};

    const active = mistakes.filter(m => !m.is_resolved);
    const resolved = mistakes.filter(m => m.is_resolved);

    const topics = ['all', ...new Set(active.map(m => m.topic_name).filter(Boolean))];

    const filteredActive = active.filter(m => {
        if (topicFilter !== 'all' && m.topic_name !== topicFilter) return false;
        return true;
    });

    return (
        <>
            <div className="mistakes-page-content">

                <div className="s-page-header">
                    <h2>Mistake Bank</h2>
                    <p>Questions you got wrong — they'll appear in your next personalized test until you answer them correctly</p>
                </div>

                {/* Stats */}
                <div className="mb-stats">
                    <div className="mb-stat-card" style={{ borderTop: '4px solid #e94560' }}>
                        <div className="mb-stat-val" style={{ color: '#e94560' }}>{active.length}</div>
                        <div className="mb-stat-label">Active Mistakes</div>
                    </div>
                    <div className="mb-stat-card" style={{ borderTop: '4px solid #16a085' }}>
                        <div className="mb-stat-val" style={{ color: '#16a085' }}>{resolved.length}</div>
                        <div className="mb-stat-label">Resolved</div>
                    </div>
                    <div className="mb-stat-card" style={{ borderTop: '4px solid #f39c12' }}>
                        <div className="mb-stat-val" style={{ color: '#f39c12' }}>
                            {active.filter(m => m.attempt_count >= 3).length}
                        </div>
                        <div className="mb-stat-label">Repeated (3+ times)</div>
                    </div>
                    <div className="mb-stat-card" style={{ borderTop: '4px solid #0f3460' }}>
                        <div className="mb-stat-val" style={{ color: '#0f3460' }}>
                            {topicBreakdown.length}
                        </div>
                        <div className="mb-stat-label">Weak Topics</div>
                    </div>
                </div>

                <div className="mb-grid">
                    <div className="mb-main">

                        {/* Tabs */}
                        <div className="mb-tabs-row">
                            <div className="mb-filter-tabs">
                                <button
                                    className={`mb-filter-tab ${activeTab === 'active' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('active')}>
                                    Active ({active.length})
                                </button>
                                <button
                                    className={`mb-filter-tab ${activeTab === 'resolved' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('resolved')}>
                                    Resolved ({resolved.length})
                                </button>
                            </div>

                            {activeTab === 'active' && active.length > 0 && (
                                <select className="mb-topic-select"
                                    value={topicFilter}
                                    onChange={e => setTopicFilter(e.target.value)}>
                                    {topics.map(t => (
                                        <option key={t} value={t}>
                                            {t === 'all' ? 'All Topics' : t}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* Active mistakes */}
                        {activeTab === 'active' && (
                            <>
                                {filteredActive.length === 0 ? (
                                    <div className="mb-empty">
                                        {active.length === 0
                                            ? '🎉 No mistakes! Keep it up!'
                                            : 'No mistakes for this topic.'}
                                    </div>
                                ) : (
                                    <>
                                        <div className="mb-info-banner">
                                            💡 These questions will be included in your next personalized test. Answer them correctly to resolve them.
                                        </div>
                                        <div className="mb-list">
                                            {filteredActive.map((m, i) => {
                                                const diffStyle = DIFF_COLORS[m.difficulty] || { bg: '#f0f0f0', color: '#555' };
                                                const catColor = CATEGORY_COLORS[m.category] || '#555';
                                                return (
                                                    <div key={m.id} className="mb-card">
                                                        <div className="mb-card-content">
                                                            <div className="mb-card-top">
                                                                <div className="mb-card-badges">
                                                                    <span className="mb-topic-badge"
                                                                        style={{ background: catColor + '18', color: catColor }}>
                                                                        {m.topic_name || 'General'}
                                                                    </span>
                                                                    <span className="mb-diff-badge"
                                                                        style={{ background: diffStyle.bg, color: diffStyle.color }}>
                                                                        {m.difficulty}
                                                                    </span>
                                                                    {m.attempt_count >= 3 && (
                                                                        <span className="mb-freq-badge">
                                                                            🔁 Wrong {m.attempt_count}×
                                                                        </span>
                                                                    )}
                                                                    {m.attempt_count === 2 && (
                                                                        <span className="mb-freq-badge" style={{ background: '#fff0f0', color: '#cc0000' }}>
                                                                            ⚠️ Wrong {m.attempt_count}×
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="mb-card-num">#{i + 1}</div>
                                                            </div>
                                                            <div className="mb-question-text">
                                                                {m.question_text}
                                                            </div>
                                                            <div className="mb-card-footer">
                                                                <span className="mb-last-seen">
                                                                    Last seen: {new Date(m.last_seen_at).toLocaleDateString('en-IN', {
                                                                        day: '2-digit', month: 'short', year: 'numeric'
                                                                    })}
                                                                </span>
                                                                <span className="mb-status-tag">
                                                                    Will appear in next personalized test →
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* Resolved mistakes */}
                        {activeTab === 'resolved' && (
                            <>
                                {resolved.length === 0 ? (
                                    <div className="mb-empty">
                                        No resolved mistakes yet. Answer your active mistakes correctly in tests to resolve them!
                                    </div>
                                ) : (
                                    <div className="mb-list">
                                        {resolved.map((m, i) => {
                                            const diffStyle = DIFF_COLORS[m.difficulty] || { bg: '#f0f0f0', color: '#555' };
                                            const catColor = CATEGORY_COLORS[m.category] || '#555';
                                            return (
                                                <div key={m.id} className="mb-card resolved">
                                                    <div className="mb-card-content">
                                                        <div className="mb-card-top">
                                                            <div className="mb-card-badges">
                                                                <span className="mb-topic-badge"
                                                                    style={{ background: catColor + '18', color: catColor }}>
                                                                    {m.topic_name || 'General'}
                                                                </span>
                                                                <span className="mb-diff-badge"
                                                                    style={{ background: diffStyle.bg, color: diffStyle.color }}>
                                                                    {m.difficulty}
                                                                </span>
                                                                <span className="mb-resolved-badge">✓ Resolved</span>
                                                            </div>
                                                        </div>
                                                        <div className="mb-question-text resolved-text">
                                                            {m.question_text}
                                                        </div>
                                                        <div className="mb-card-footer">
                                                            <span className="mb-last-seen">
                                                                Answered correctly in test
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Right sidebar */}
                    <div className="mb-sidebar">

                        {/* Weak topics */}
                        <div className="mb-sidebar-card">
                            <div className="mb-sidebar-title">Weak Topics</div>
                            {topicBreakdown.length === 0 ? (
                                <div style={{ fontSize: 13, color: '#aaa', padding: '12px 0' }}>
                                    No mistakes yet! 🎉
                                </div>
                            ) : topicBreakdown.map((t, i) => {
                                const maxCount = topicBreakdown[0]?.count || 1;
                                const pct = Math.round((t.count / maxCount) * 100);
                                const color = CATEGORY_COLORS[t.category] || '#0f3460';
                                return (
                                    <div key={i} className="mb-topic-row"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            setActiveTab('active');
                                            setTopicFilter(topicFilter === t.topic ? 'all' : t.topic);
                                        }}>
                                        <div className="mb-topic-name"
                                            style={{
                                                color: topicFilter === t.topic ? color : '#444',
                                                fontWeight: topicFilter === t.topic ? 700 : 400
                                            }}>
                                            {t.topic || 'General'}
                                        </div>
                                        <div className="mb-topic-bar-bg">
                                            <div className="mb-topic-bar"
                                                style={{ width: `${pct}%`, background: color }} />
                                        </div>
                                        <div className="mb-topic-count" style={{ color }}>
                                            {t.count}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* How it works */}
                        <div className="mb-sidebar-card tips">
                            <div className="mb-sidebar-title">⚡ How it works</div>
                            {[
                                'Wrong answers are automatically saved here',
                                'These questions are added to your next personalized test',
                                'Answer correctly in the next test to resolve them',
                                'Repeated mistakes (3+) are highlighted',
                                'Focus on your weak topics shown above',
                            ].map((tip, i) => (
                                <div key={i} className="mb-tip">
                                    <span className="mb-tip-num">{i + 1}</span>
                                    <span>{tip}</span>
                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default MistakeBank;
